import { useMemo, useState, useEffect, useRef } from "react";
import { Stack, IconButton } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppQuestion, ChatAppResponse, getCitationFilePath, MessageFeedback, SpeechConfig, Citation } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";
import AnswerFeedback from "./AnswerFeedback";

interface Props {
    userQuestion?: ChatAppQuestion;
    answer: ChatAppResponse;
    index: number;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (citation: Citation) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    onFeedbackSent?: (feedback: MessageFeedback) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
    streamingReasoning?: string;
    streamingResearchReasoning?: string;
}

export const Answer = ({
    userQuestion,
    answer,
    index,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    onFeedbackSent,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser,
    streamingReasoning,
    streamingResearchReasoning
}: Props) => {
    const followupQuestions = answer.context?.followup_questions;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming, onCitationClicked), [answer]);
    const { t } = useTranslation();
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    const answerTextRef = useRef<HTMLDivElement>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!answerTextRef.current) return;
        const anchors = answerTextRef.current.querySelectorAll<HTMLAnchorElement>("a.supContainer[data-citation-index]");
        const handlers: Array<{ el: HTMLAnchorElement; handler: () => void }> = [];
        anchors.forEach(el => {
            const idx = parseInt(el.getAttribute("data-citation-index") ?? "-1", 10);
            const citation = parsedAnswer.citations[idx];
            if (citation) {
                const handler = () => onCitationClicked(citation);
                el.addEventListener("click", handler);
                handlers.push({ el, handler });
            }
        });
        return () => {
            handlers.forEach(({ el, handler }) => el.removeEventListener("click", handler));
        };
    }, [sanitizedAnswerHtml, parsedAnswer.citations, onCitationClicked]);

    const reasoningThought = answer.context?.thoughts?.find(th => th.title === "Model reasoning");
    const reasoningText = reasoningThought ? String(reasoningThought.description ?? "") : "";
    // During streaming, show the live reasoning text; after streaming, fall back to the thought entry
    const effectiveReasoningText = isStreaming && streamingReasoning ? streamingReasoning : reasoningText;

    const toolSteps = answer.context?.thoughts?.filter(
        th => th.title === "search_sources" || th.title === "fetch_document" || th.title === "search_abb_library"
    ) ?? [];

    const showThinking = toolSteps.length > 0 || effectiveReasoningText.length > 0 || !!streamingResearchReasoning;

    // Auto-open the Thinking section the first time there is something to show,
    // but only during active streaming — completed answers start collapsed.
    const hasAutoOpenedRef = useRef(false);
    const [thinkingOpen, setThinkingOpen] = useState(false);
    useEffect(() => {
        if (!isStreaming) return;
        if (!hasAutoOpenedRef.current && (toolSteps.length > 0 || effectiveReasoningText.length > 0 || !!streamingResearchReasoning)) {
            hasAutoOpenedRef.current = true;
            setThinkingOpen(true);
        }
    }, [isStreaming, toolSteps.length, effectiveReasoningText, streamingResearchReasoning]);

    // Close the Thinking section when the answer content starts being generated
    const prevContentRef = useRef(answer.message?.content);
    useEffect(() => {
        if (isStreaming && !prevContentRef.current && answer.message?.content) {
            setThinkingOpen(false);
        }
        prevContentRef.current = answer.message?.content;
    }, [isStreaming, answer.message?.content]);

    const formatReasoningMarkdown = (text: string) =>
        text.replace(/(?<!\n)\s*(\*\*[^*]+\*\*)/g, "\n\n$1");

    const handleCopy = () => {
        // Single replace to remove all HTML tags to remove the citations
        const textToCopy = sanitizedAnswerHtml.replace(/<a [^>]*><sup>\d+<\/sup><\/a>|<[^>]+>/g, "");

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        {/*<IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: copied ? "CheckMark" : "Copy" }}
                            title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            ariaLabel={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            onClick={handleCopy}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "Lightbulb" }}
                            title={t("tooltips.showThoughtProcess")}
                            ariaLabel={t("tooltips.showThoughtProcess")}
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.context.thoughts?.length || isStreaming}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "ClipboardList" }}
                            title={t("tooltips.showSupportingContent")}
                            ariaLabel={t("tooltips.showSupportingContent")}
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.context.data_points || isStreaming}
                        />*/}
                        {showSpeechOutputAzure && (
                            <SpeechOutputAzure answer={sanitizedAnswerHtml} index={index} speechConfig={speechConfig} isStreaming={isStreaming} />
                        )}
                        {showSpeechOutputBrowser && <SpeechOutputBrowser answer={sanitizedAnswerHtml} />}
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                {showThinking && (
                    <details className={styles.reasoningDetails} open={thinkingOpen} onToggle={e => setThinkingOpen((e.target as HTMLDetailsElement).open)}>
                        <summary className={styles.reasoningSummary}>Thinking</summary>
                        <div className={styles.reasoningBody}>
                            {toolSteps.map((step, i) => (
                                <div key={i} className={styles.toolStep}>
                                    {step.props?.reasoning && (
                                        <div className={styles.toolStepReasoning}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{formatReasoningMarkdown(String(step.props.reasoning))}</ReactMarkdown>
                                        </div>
                                    )}
                                    <div className={styles.toolStepHeader}>
                                        <span className={styles.toolStepIcon}>
                                            {step.title === "fetch_document" ? "📄" : "🔍"}
                                        </span>
                                        <span className={styles.toolStepText}>{String(step.description ?? "")}</span>
                                        {step.title === "search_abb_library" && (
                                            <span className={styles.toolStepCount}>ABB Library (preview)</span>
                                        )}
                                        {step.title !== "search_abb_library" && step.props?.results_count !== undefined && (
                                            <span className={styles.toolStepCount}>{step.props.results_count} chunks</span>
                                        )}
                                        {step.props?.chunks_count !== undefined && (
                                            <span className={styles.toolStepCount}>{step.props.chunks_count} chunks</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {streamingResearchReasoning && (
                                <div className={styles.toolStepReasoning}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{formatReasoningMarkdown(streamingResearchReasoning)}</ReactMarkdown>
                                </div>
                            )}
                            {reasoningText && !isStreaming && (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{formatReasoningMarkdown(reasoningText)}</ReactMarkdown>
                            )}
                            {effectiveReasoningText && isStreaming && (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{formatReasoningMarkdown(effectiveReasoningText)}</ReactMarkdown>
                            )}
                        </div>
                    </details>
                )}
                <div className={styles.answerText} ref={answerTextRef}>
                    <ReactMarkdown children={sanitizedAnswerHtml} rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} />
                </div>
            </Stack.Item>

            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>{t("citationWithColon")}</span>
                        {parsedAnswer.citations.map((citation, i) => {
                            // Strip out the image filename in parentheses if it exists
                            const displayText = citation.filename.replace(/\([^)]*\)$/, "");
                            return (
                                <a key={i} className={styles.citation} title={citation.filename} onClick={() => onCitationClicked(citation)}>
                                    {`${i + 1}. ${displayText}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>{t("followupQuestions")}</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
            {userQuestion && onFeedbackSent && (
             <AnswerFeedback
                questionAnswerPair={[ userQuestion, answer]}
                isStreaming={isStreaming}
                hasNegativeFeedback={answer.message.hasNegativeFeedback}
                hasPositiveFeedback={answer.message.hasPositiveFeedback}
                onFeedbackSent={feedback => onFeedbackSent(feedback)}
            />
            )}
        </Stack>
    );
};
